import { useEffect, useState, useCallback } from 'react';

import dayjs from 'dayjs';

import AmenityHeader from './AmenityHeader.js';
import DateSelector from './DateSelector.js';
import TimeSlotSelector from './TimeSlotSelector.js';

const AmenityBookingCalendar = ({
  selectedAmenity,
  amenitySettings,
  amenitiesData,
  selectedDate,
  selectedSlots,
  onDateChange,
  onSlotsChange,
  onBooking
}) => {
  const [timeSlots, setTimeSlots] = useState([]);

  useEffect(() => {
    if (selectedAmenity) {
      setTimeSlots([]);
      console.log('Selected Amenity data:', amenitiesData);
    }
  }, [selectedAmenity, amenitiesData]);

  // Helper functions
  const isDayAllowed = (date) => {
    if (!amenitySettings?.allowedDaysOfWeek) return true;
    const dayName = date.format('dddd').toLowerCase();
    return amenitySettings.allowedDaysOfWeek.includes(dayName);
  };

  const isDateWithinBookingWindow = (date) => {
    if (!amenitySettings?.bookingWindowDays) return true;
    const today = dayjs();
    const maxDate = today.add(amenitySettings.bookingWindowDays, 'day');
    return date.isBefore(maxDate) || date.isSame(maxDate, 'day');
  };

  const shouldDisableDate = (date) => {
    return !isDayAllowed(date) || !isDateWithinBookingWindow(date);
  };

  const getTimeSlotsFromRange = (startTime, endTime, slotSizeMinutes = 30) => {
    const slots = [];
    let current = dayjs(`2000-01-01 ${startTime}`);
    const end = dayjs(`2000-01-01 ${endTime}`);

    while (current.isBefore(end) || current.isSame(end)) {
      slots.push(current.format('HH:mm'));
      current = current.add(slotSizeMinutes, 'minute');
    }

    return slots;
  };

  const getBookedSlotsForDate = useCallback((date) => {
    if (!amenitiesData || !Array.isArray(amenitiesData)) return [];

    const bookingsForDate = amenitiesData.filter(
      b => dayjs(b.bookingDate).format("YYYY-MM-DD") === date.format("YYYY-MM-DD")
    );

    const slotSize = amenitySettings?.slotSizeMinutes || 30;

    const bookedSlots = bookingsForDate
      .map(b => getTimeSlotsFromRange(b.startTime, b.endTime, slotSize))
      .flat();

    return bookedSlots;
  }, [amenitiesData, amenitySettings]);

  const generateTimeSlotsForDate = useCallback((date) => {
    if (!amenitySettings?.allowedTimeSlots) return [];

    const bookedSlotsForDate = getBookedSlotsForDate(date);
    const slotSizeMinutes = amenitySettings.slotSizeMinutes || 30;
    const slots = [];

    amenitySettings.allowedTimeSlots.forEach((range, rangeIndex) => {
      const start = dayjs(`2000-01-01 ${range.startTime}`);
      const end = dayjs(`2000-01-01 ${range.endTime}`);
      let current = start;

      while (current.isBefore(end) || current.isSame(end)) {
        const timeStr = current.format('HH:mm');
        const slotEnd = current.add(slotSizeMinutes, 'minute');

        if (current.isSame(end) || slotEnd.isSame(end) || slotEnd.isBefore(end)) {
          const isAvailable = !bookedSlotsForDate.includes(timeStr);

          slots.push({
            time: timeStr,
            available: isAvailable,
            groupId: rangeIndex,
          });
        }

        current = current.add(slotSizeMinutes, 'minute');
      }
    });

    return slots.sort((a, b) => a.time.localeCompare(b.time));
  }, [amenitySettings, getBookedSlotsForDate]);

  // Update time slots when data changes
  useEffect(() => {
    if (!selectedAmenity || !selectedDate) {
      setTimeSlots([]);
      return;
    }

    console.log('Generating time slots for date:', selectedDate.format('YYYY-MM-DD'));
    console.log('Amenities data:', amenitiesData);
    console.log('Amenity settings:', amenitySettings);

    const newTimeSlots = generateTimeSlotsForDate(selectedDate);
    console.log('Generated time slots:', newTimeSlots);
    setTimeSlots(newTimeSlots);

  }, [amenitiesData, selectedDate, selectedAmenity, amenitySettings, generateTimeSlotsForDate]);

  return (
    <div className="space-y-8">
      <AmenityHeader
        selectedAmenity={selectedAmenity}
        amenitySettings={amenitySettings}
      />

      <div className="grid lg:grid-cols-2 gap-8">
        <DateSelector
          selectedDate={selectedDate}
          onDateChange={onDateChange}
          shouldDisableDate={shouldDisableDate}
        />

        <TimeSlotSelector
          selectedDate={selectedDate}
          timeSlots={timeSlots}
          selectedSlots={selectedSlots}
          amenitySettings={amenitySettings}
          shouldDisableDate={shouldDisableDate}
          onSlotsChange={onSlotsChange}
        />
      </div>

      <div className="flex justify-center pt-6 border-t border-gray-200">
        <button
          disabled={!selectedDate || selectedSlots.length === 0 || shouldDisableDate(selectedDate)}
          onClick={onBooking}
          className={`
            px-8 py-4 rounded-lg font-semibold text-white transition-all duration-200 text-lg
            ${(!selectedDate || selectedSlots.length === 0 || shouldDisableDate(selectedDate))
              ? 'cursor-not-allowed transform scale-95 opacity-60'
              : 'hover:shadow-lg transform hover:scale-105'}
          `}
          style={{
            backgroundColor: (!selectedDate || selectedSlots.length === 0 || shouldDisableDate(selectedDate))
              ? '#818FB4'
              : '#C86B2C'
          }}
        >
          {!selectedDate
            ? 'Select Date & Time'
            : shouldDisableDate(selectedDate)
              ? 'Date Not Available'
              : selectedSlots.length === 0
                ? 'Select Time Slot'
                : 'Book Amenity'
          }
        </button>
      </div>
    </div>
  );
};

export default AmenityBookingCalendar;